import React, { useState, useEffect, useRef } from "react";
import PropTypes from 'prop-types';
import Modal from '../Modal/Modal';
import './Login.css';
import purify from "dompurify";
import { useNavigate } from 'react-router-dom';

const Login = React.memo( 
  ({
    isFetching,
    errorMessage,
    fetchAccessToken,
    fetchUserData,
    verifyOtp,
    otpToken,
    resendOtp,
    accessToken,
    otpAttemptCount
  }) => {
    const [loginId, setLoginId] = useState("");
    const [remember, setRemember] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [resendNotification, setResendNotification] = useState(false);
    const [countdown, setCountdown] = useState(30);
    const [localErrorMessage, setLocalErrorMessage] = useState('');
    const loginIdRef = useRef(null);
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [isResending, setIsResending] = useState(false);

    /* -----------------------------------------------
    * OTPトークンが取得されたらOTP入力画面へ切替
    * ----------------------------------------------- */
    useEffect(() => {
      if (otpToken) {
        localStorage.setItem("otpToken", otpToken);
        setShowOtp(true);
      }
    }, [otpToken]);

    // ログアウト後のリダイレクト制御用フラグを初期化する。
    useEffect(() => {
      const lastAction = localStorage.getItem("lastAction");
      if (lastAction === "logout") {
        localStorage.removeItem("lastAction");
      }
    }, []);
    
    /* -----------------------------------------------
     * 初期表示時の処理
     * - 「ログインID記憶」設定の読み込み
     * - 既にアクセストークンがある場合はユーザー情報取得
     * ----------------------------------------------- */
    useEffect(() => {
      const storedLoginId = localStorage.getItem('loginId');
      const storedRemember = localStorage.getItem('remember') === 'true';
      setRemember(storedRemember);
      if (storedRemember) setLoginId(storedLoginId || '');

      const accessTokenLocal = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (accessToken || accessTokenLocal) {
        const tokenToUse = accessToken || accessTokenLocal;

        if (tokenToUse && refreshToken) {
          fetchUserData(tokenToUse);
          const redirectPath =
            localStorage.getItem("redirectAfterLogin") || "/";

          localStorage.removeItem("redirectAfterLogin");
          navigate(redirectPath);
        }
      }
    }, [accessToken, fetchUserData, navigate]);

    useEffect(() => {
      if (otpAttemptCount >= 3) {
        setShowOtp(false);
        setCountdown(30);
        setResendNotification(false);
        setOtpCode("");

        localStorage.removeItem("otpToken");
        // ログイン画面へ遷移
        navigate("/login");
      }

    // eslint-disable-next-line
    }, [otpAttemptCount]);

    const isValidEmail = (email) => {
      const regex =
        /^[a-zA-Z0-9]+([._-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-]?[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
      return regex.test(email);
    };

    // ログインボタンクリック時
    const handleLogin = async (e) => {
      e.preventDefault();

      if (!loginId) {
        setLocalErrorMessage("ユーザーIDが入力されていません。");
        return;
      }

      if (!isValidEmail(loginId)) {
        setLocalErrorMessage("メールアドレスの形式が正しくありません。");
        return;
      }

      setLocalErrorMessage("");

      try {
        await fetchAccessToken({
          login_id: loginId,
        });
      } catch (error) {
        if (remember) {
          loginIdRef.current?.select();
        } else {
          setLoginId('');
          loginIdRef.current?.focus();
        }
      }

      // 「ログインIDを記憶する」設定の保持
      if (remember) {
        localStorage.setItem('loginId', loginId);
        localStorage.setItem('remember', remember);
      } else {
        localStorage.removeItem('loginId');
        localStorage.removeItem('remember');
      };
    };

    //OTP認証ボタンクリック時
    const handleVerifyOtp = async (e) => {
      e.preventDefault();

      if (!otpCode.trim()) {
        setModalMessage("認証コードを入力してください。");
        setIsModalOpen(true);
        return;
      }
      setResendNotification(false);
      const effectiveOtpToken = otpToken || localStorage.getItem("otpToken");
      try {
        const res = await verifyOtp(effectiveOtpToken, otpCode);

        if (!res?.success) {
          throw new Error(res.error_code || 'OTP_ERROR');
        }

      } catch (error) {
        setOtpCode('');
        setResendNotification(true);
      }

    };

    //OTP入力値変更時
    const handleOtpChange = (e) => {
      setOtpCode(e.target.value);
    };

    // 「再送」リンククリック時
    const handleResendClick = async () => {
      if (isResending) return;

      setIsResending(true);

      try {
        await resendOtp(otpToken);

        setCountdown(30);
        setOtpCode('');
        setResendNotification(false);
        setModalMessage("認証コードを再送信しました。");
        setIsModalOpen(true);
      } catch (error) {
        console.error("resend OTP error:", error);
        setModalMessage("認証コードの再送信に失敗しました。");
        setIsModalOpen(true);
      } finally {
        setTimeout(() => setIsResending(false), 30000);
      }
    };

    //「ログインIDを記憶する」チェック変更時
    const handleCheckboxChange = (e) => {
      setRemember(e.target.checked);
    }; 

    useEffect(() => {
      const interval = setInterval(() => {
        if (otpToken || showOtp) {
          if (countdown > 0) {
            setCountdown(countdown - 1);
          } else {
            clearInterval(interval);
            setResendNotification(true);
          }
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [countdown, otpToken, showOtp]);

    return (
      <div className="lay-login">
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            message={modalMessage}
          />
        )}
        <div className="lay-login__sub">
          <div className="center">
            <img src="/img/common/navi/logo.png" alt="Logo" className="center" />
          </div>
          <div className="hpc15">
            <span className="login-title">発番システム</span>
            <span className="login-subtitle">HPC-15</span>
          </div>

          {!showOtp ? (
            <div className="login-form">
              <form onSubmit={handleLogin}>
                <label className="login-label">ユーザーID:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="メールアドレス"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value.toLowerCase())}
                />

                <div className="checkbox-frame">
                  <label>
                    <input
                      type="checkbox"
                      id="remember"
                      checked={remember}
                      name="checkbox"
                      className="checkbox-input"
                      onChange={handleCheckboxChange}
                    />
                    <span className="checkbox-parts">
                       ユーザーIDを記憶する
                    </span>
                  </label>
                </div>

                {(localErrorMessage || errorMessage) && (
                  <p className="error" dangerouslySetInnerHTML={{ __html: purify.sanitize(localErrorMessage || errorMessage) }} />
                )}

                <button
                  type="submit"
                  className="login-button"
                  disabled={isFetching}
                >
                  ログイン
                  {isFetching && <div className="login-spinner"></div>}
                </button>
              </form>
            </div>
          ) : (
            <div className="login-form">
              {(localErrorMessage || errorMessage) && (
                <p className="error" dangerouslySetInnerHTML={{ __html: purify.sanitize(localErrorMessage || errorMessage) }} />
              )}
              <form id="otp-form" method="post" onSubmit={handleVerifyOtp}>
                <label className="login-label">認証コード:</label>
                <input
                  type="text"
                  className="form-control_otpcode"
                  id="otp_code"
                  name="otp_code"
                  maxLength="6"
                  autoComplete="off"
                  value={otpCode}
                  onChange={handleOtpChange}
                />
                <div className="otp-check-div" style={{ marginTop: "20px"}}>
                  <button type="submit" className="btn login-button" style={{width: "100%"}}>認証{isFetching && <div className="login-spinner" ></div>}</button>
                </div>
              </form>
              {resendNotification && (
                <div id="resend-otp">
                  <span>
                    認証コードが受信できない場合、
                    <em
                      onClick={handleResendClick}
                      style={{
                        cursor: isResending ? 'not-allowed' : 'pointer',
                        textDecoration: isResending ? 'none' : 'underline',
                        opacity: isResending ? 0.5 : 1,
                      }}
                    >
                      {isResending ? '再送中...' : 'こちら'}
                    </em>
                    で再試行してください。
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Login.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  otpToken: PropTypes.string,
  accessToken: PropTypes.string,
  refreshToken: PropTypes.string,
  errorCode: PropTypes.string,
  errorMessage: PropTypes.string,
  fetchAccessToken: PropTypes.func.isRequired,
  fetchUserData: PropTypes.func.isRequired,
  verifyOtp: PropTypes.func.isRequired,
  resendOtp: PropTypes.func.isRequired,
  otpAttemptCount: PropTypes.number,
};

export default Login;
